import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from "material-ui-color";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  IconButton,
  InputAdornment,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  colorAdorment: {
    width: 20,
    height: 20,
  },

  avisoTempo: {
    height: 24,
  },
}));

const TagSchema = Yup.object().shape({
  name: Yup.string().min(3, "Mensagem muito curta").required("Obrigatório"),
});

const TagModal = ({ open, onClose, tagId, reload }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  const initialState = {
    name: "",
    color: "",
    isContact: false,
    isKanban: false,
    kanbantempoverde: 0,
    kanbantempoamarelo: 0,
    kanbantempovermelho: 0,
  };

  const temposVerde = [
    { kanbantempoverde: "0", name: "Não utilizar" },
    { kanbantempoverde: "30", name: "30 minutos" },
    { kanbantempoverde: "60", name: "1 hora" },
    { kanbantempoverde: "120", name: "2 horas" },
    { kanbantempoverde: "180", name: "3 horas" },
    { kanbantempoverde: "360", name: "6 horas" },
    { kanbantempoverde: "720", name: "12 horas" },
    { kanbantempoverde: "1440", name: "24 horas" },
    { kanbantempoverde: "2160", name: "36 horas" },
    { kanbantempoverde: "2880", name: "48 horas" },
    { kanbantempoverde: "3600", name: "60 horas" },
    { kanbantempoverde: "4320", name: "72 horas" },
  ];

  const temposAmarelo = [
    { kanbantempoamarelo: "0", name: "Não utilizar" },
    { kanbantempoamarelo: "30", name: "30 minutos" },
    { kanbantempoamarelo: "60", name: "1 hora" },
    { kanbantempoamarelo: "120", name: "2 horas" },
    { kanbantempoamarelo: "180", name: "3 horas" },
    { kanbantempoamarelo: "360", name: "6 horas" },
    { kanbantempoamarelo: "720", name: "12 horas" },
    { kanbantempoamarelo: "1440", name: "24 horas" },
    { kanbantempoamarelo: "2160", name: "36 horas" },
    { kanbantempoamarelo: "2880", name: "48 horas" },
    { kanbantempoamarelo: "3600", name: "60 horas" },
    { kanbantempoamarelo: "4320", name: "72 horas" },
  ];

  const temposVermelho = [
    { kanbantempovermelho: "0", name: "Não utilizar" },
    { kanbantempovermelho: "30", name: "30 minutos" },
    { kanbantempovermelho: "60", name: "1 hora" },
    { kanbantempovermelho: "120", name: "2 horas" },
    { kanbantempovermelho: "180", name: "3 horas" },
    { kanbantempovermelho: "360", name: "6 horas" },
    { kanbantempovermelho: "720", name: "12 horas" },
    { kanbantempovermelho: "1440", name: "24 horas" },
    { kanbantempovermelho: "2160", name: "36 horas" },
    { kanbantempovermelho: "2880", name: "48 horas" },
    { kanbantempovermelho: "3600", name: "60 horas" },
    { kanbantempovermelho: "4320", name: "72 horas" },
  ];

  const [tag, setTag] = useState(initialState);

  useEffect(() => {
    try {
      (async () => {
        if (!tagId) return;
        const { data } = await api.get(`/tags/${tagId}`);
        setTag((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorPickerModalOpen(false);
    onClose();
  };

  const handleSaveTag = async (values) => {
    const tagData = { ...values, userId: user.id };
    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post("/tags", tagData);
      }
      toast.success(i18n.t("tagModal.success"));
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {tagId
            ? `${i18n.t("tagModal.title.edit")}`
            : `${i18n.t("tagModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={tag}
          enableReinitialize={true}
          validationSchema={TagSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveTag(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("tagModal.form.name")}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    onChange={(e) =>
                      setTag((prev) => ({ ...prev, name: e.target.value }))
                    }
                    fullWidth
                  />
                </div>

                <br />
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={i18n.t("tagModal.form.color")}
                    name="color"
                    id="color"
                    error={touched.color && Boolean(errors.color)}
                    helperText={touched.color && errors.color}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <div
                            style={{ backgroundColor: values.color }}
                            className={classes.colorAdorment}
                          ></div>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <IconButton
                          size="small"
                          color="default"
                          onClick={() =>
                            setColorPickerModalOpen(!colorPickerModalOpen)
                          }
                        >
                          <Colorize />
                        </IconButton>
                      ),
                    }}
                    variant="outlined"
                    margin="dense"
                  />
                </div>

                {colorPickerModalOpen && (
                  <div>
                    <ColorBox
                      disableAlpha={true}
                      hslGradient={false}
                      style={{ margin: "20px auto 0" }}
                      value={tag.color}
                      onChange={(val) => {
                        setTag((prev) => ({ ...prev, color: `#${val.hex}` }));
                      }}
                    />
                  </div>
                )}

                <br />
                <div className={classes.multFieldLine}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.maxWidth}
                    fullWidth
                  >
                    <InputLabel>Kanban "Tempo Alerta Verde"</InputLabel>
                    <Field
                      as={Select}
                      name="kanbantempoverde"
                      id="kanbantempoverde"
                      label='Kanban "Tempo Alerta Verde"'
                    >
                      {temposVerde.map((temposVerde) => (
                        <MenuItem
                          className={classes.avisoTempo}
                          key={temposVerde.kanbantempoverde}
                          value={temposVerde.kanbantempoverde}
                        >
                          {temposVerde.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </div>

                <div className={classes.multFieldLine}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.maxWidth}
                    fullWidth
                  >
                    <InputLabel>Kanban "Tempo Alerta Amarelo"</InputLabel>
                    <Field
                      as={Select}
                      name="kanbantempoamarelo"
                      id="kanbantempoamarelo"
                      label='Kanban "Tempo Alerta Amarelo"'
                    >
                      {temposAmarelo.map((temposAmarelo) => (
                        <MenuItem
                          className={classes.avisoTempo}
                          key={temposAmarelo.kanbantempoamarelo}
                          value={temposAmarelo.kanbantempoamarelo}
                        >
                          {temposAmarelo.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </div>

                <div className={classes.multFieldLine}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.maxWidth}
                    fullWidth
                  >
                    <InputLabel>Kanban "Tempo Alerta Vermelho"</InputLabel>
                    <Field
                      as={Select}
                      name="kanbantempovermelho"
                      id="kanbantempovermelho"
                      label='Kanban "Tempo Alerta Vermelho"'
                    >
                      {temposVermelho.map((temposVermelho) => (
                        <MenuItem
                          className={classes.avisoTempo}
                          key={temposVermelho.kanbantempovermelho}
                          value={temposVermelho.kanbantempovermelho}
                        >
                          {temposVermelho.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </div>

                <br />
                <div className={classes.multFieldLine}>
                  <FormControlLabel
                    style={{ marginLeft: "-1px" }}
                    control={
                      <Field type="checkbox" name="isContact" id="isContact" />
                    }
                    label="Para Contato"
                    labelPlacement="end"
                  />

                  <FormControlLabel
                    style={{ marginLeft: "30px" }}
                    control={
                      <Field type="checkbox" name="isKanban" id="isKanban" />
                    }
                    label="Para Kanban"
                    labelPlacement="end"
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("tagModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {tagId
                    ? `${i18n.t("tagModal.buttons.okEdit")}`
                    : `${i18n.t("tagModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TagModal;
